/* eslint-disable */
import React, { useEffect, useState } from 'react';
import ReactDOMServer from 'react-dom/server'
import Moment from 'moment-timezone';
import { extendMoment } from 'moment-range';
import jsPDF from 'jspdf';
import * as html2canvas from 'html2canvas';
import {
    Container,
    Grid,
    Segment,
    Header,
    Divider,
    Table,
    Button,
    Modal,
    Icon
} from 'semantic-ui-react';
import { useCurrentUser } from '../../../_services/hooks';
import {
    useCustomer,
    useAonAccountForCustomer,
    useAonStatementForAccount as useAonStatementForAccount
} from '../../../_services';
import { history } from '../../../_helpers';
import { set } from 'lodash';

const moment = extendMoment(Moment);

const amountInPence = amount => {
    if (isNaN(amount)) return 0;

    if (amount < 0)
        return 0 - Math.floor((0 - amount) / 1000);
    else
        return Math.floor(amount / 1000);
};

const formatCurrency = (currency, amountPounds) => {
    const absAmountPounds = Math.abs(amountPounds);
    return (
        (amountPounds < 0 ? '-' : '') +
        currency +
        absAmountPounds.toFixed(2) 
    );
};

const StatusBadge = ({ status }) => {
    const getStatusConfig = (status) => {
        const baseStyle = {
            display: 'inline-block',
            padding: '0 10px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '700',
            height: '20px',
            lineHeight: '20px',
            textAlign: 'center'
        };

        // Null/undefined means Scheduled
        if (!status || status === null) {
            return {
                style: {
                    ...baseStyle,
                    backgroundColor: '#2185d0',
                    color: '#fff'
                },
                label: 'Scheduled'
            };
        }

        switch (status.toUpperCase()) {
            case 'RESOLVED':
                return {
                    style: {
                        ...baseStyle,
                        backgroundColor: '#21ba45',
                        color: '#fff'
                    },
                    label: 'Resolved'
                };
            case 'PENDING':
                return {
                    style: {
                        ...baseStyle,
                        backgroundColor: '#fbbd08',
                        color: '#00205b'
                    },
                    label: 'Pending'
                };
            case 'FAILED':
                return {
                    style: {
                        ...baseStyle,
                        backgroundColor: '#db2828',
                        color: '#fff'
                    },
                    label: 'Failed'
                };
            default:
                return {
                    style: {
                        ...baseStyle,
                        backgroundColor: '#2185d0',
                        color: '#fff'
                    },
                    label: 'Scheduled'
                };
        }
    };

    const config = getStatusConfig(status);

    return (
        <span style={config.style}>
            {config.label}
        </span>
    );
};

const TransactionDetails = ({ transaction }) => {
    const detailStyle = {
        fontSize: '12px',
        color: '#00205b',
        padding: '0 20px'
    };

    const rowStyle = {
        display: 'flex',
        gap: '10px',
        height: '20px',
        alignItems: 'center',
        marginBottom: '0'
    };

    const labelStyle = {
        fontWeight: '700',
        color: '#00205b'
    };

    const locationBadgeStyle = (isHome) => ({
        display: 'inline-block',
        padding: '0 10px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '700',
        height: '20px',
        lineHeight: '20px',
        backgroundColor: isHome ? '#00C5C2' : '#84D012',
        color: isHome ? '#fff' : '#00205b',
        textAlign: 'center'
    });

    // METERED_CHARGE - Show consumption, location, tariff
    if (transaction.transaction_type === 'METERED_CHARGE') {
        return (
            <div style={detailStyle}>
                {transaction.consumption_kwh !== null && transaction.consumption_kwh !== undefined && (
                    <div style={rowStyle}>
                        <span style={labelStyle}>Energy:</span>
                        <span>{transaction.consumption_kwh} kWh{transaction.avg_tariff && ` @£${parseFloat(transaction.avg_tariff).toFixed(2)}/kWh`}</span>
                    </div>
                )}
                {transaction.location && (
                    <div style={rowStyle}>
                        <span style={labelStyle}>Location:</span>
                        <span style={{ flex: 1 }}>{transaction.location}</span>
                        {transaction.is_host_at_home !== null && (
                            <span style={locationBadgeStyle(transaction.is_host_at_home)}>
                                {transaction.is_host_at_home ? 'Home' : 'Away'}
                            </span>
                        )}
                    </div>
                )}
                {transaction.usage_from && transaction.usage_to && (
                    <div style={rowStyle}>
                        <span style={labelStyle}>Session:</span>
                        <span>
                            {new Date(transaction.usage_from).toLocaleTimeString('en-GB', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                            })}
                            {' - '}
                            {new Date(transaction.usage_to).toLocaleTimeString('en-GB', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                            })}
                        </span>
                    </div>
                )}
                {transaction.vat_calculated && (
                    <div style={rowStyle}>
                        <span style={labelStyle}>VAT:</span>
                        <span>
                            {formatCurrency('£', amountInPence(transaction.vat_calculated) / 100)}
                            {transaction.vat_rate && ` (${(parseFloat(transaction.vat_rate) * 100).toFixed(0)}%)`}
                        </span>
                    </div>
                )}
            </div>
        );
    }

    // REIMBURSEMENT - Show error margin
    if (transaction.transaction_type === 'REIMBURSEMENT') {
        return (
            <div style={detailStyle}>
                {transaction.error_margin && (
                    <div style={rowStyle}>
                        <span style={labelStyle}>Margin:</span>
                        <span>+{(parseFloat(transaction.error_margin) * 100).toFixed(1)}% metered accuracy adjustment</span>
                    </div>
                )}
                {transaction.credit_desc && (
                    <div style={{ ...rowStyle, fontStyle: 'italic' }}>
                        {transaction.credit_desc}
                    </div>
                )}
            </div>
        );
    }

    // ONE_OFF_CHARGE
    if (transaction.transaction_type === 'ONE_OFF_CHARGE') {
        return (
            <div style={detailStyle}>
                {transaction.one_off_charge_type && (
                    <div style={rowStyle}>
                        <span style={labelStyle}>Type:</span>
                        <span>{transaction.one_off_charge_type}</span>
                    </div>
                )}
                {transaction.one_off_charge_desc && (
                    <div style={rowStyle}>
                        {transaction.one_off_charge_desc}
                    </div>
                )}
                {transaction.vat_calculated && (
                <div style={rowStyle}>
                    <span style={labelStyle}>VAT:</span>
                    <span>
                        {formatCurrency('£', amountInPence(transaction.vat_calculated) / 100)}
                        {transaction.vat_rate && ` (${(parseFloat(transaction.vat_rate) * 100).toFixed(0)}%)`}
                    </span>
                </div>
                )}
            </div>
        );
    }

    if (transaction.transaction_type == 'FIXED_MONTHLY_CHARGE') {
        return (
            <div style={detailStyle}>
                {transaction.vat_calculated && (
                <div style={rowStyle}>
                    <span style={labelStyle}>VAT:</span>
                    <span>
                        {formatCurrency('£', amountInPence(transaction.vat_calculated) / 100)}
                        {transaction.vat_rate && ` (${(parseFloat(transaction.vat_rate) * 100).toFixed(0)}%)`}
                    </span>
                </div>
                )}
            </div>
        );
    }

    // TARIFF_CORRECTION
    if (transaction.transaction_type === 'TARIFF_CORRECTION') {
        return (
            <div style={detailStyle}>
                <div style={rowStyle}>
                    <span style={labelStyle}>Old Rate:</span>
                    <span>{formatCurrency('£', amountInPence(transaction.old_tariff_amount) / 100)}</span>
                    <span style={{ margin: '0 0.5em' }}>→</span>
                    <span style={labelStyle}>New Rate:</span>
                    <span>{formatCurrency('£', amountInPence(transaction.new_tariff_amount) / 100)}</span>
                </div>
            </div>
        );
    }

    return null;
};

const calculateNetSummary = (statements) => {
    const summary = {
        resolved: { 
            charges: 0, 
            credits: 0, 
            paid: 0, 
            reimbursed: 0 
        },
        unresolved: { 
            paymentsDue: 0, 
            reimbursementsDue: 0 
        }
    };
    
    console.log('=== Starting Net Summary Calculation ===');
    console.log('Total parent statements:', statements.length);
    
    // Iterate through parent transactions and their children
    statements.forEach((parent, idx) => {
        const parentType = parent.transaction_type;
        const parentStatus = parent.friendly_status?.toUpperCase();
        const isParentResolved = parentStatus === 'RESOLVED';
        
        console.log(`\nParent ${idx + 1}: ${parentType}, Status: ${parentStatus}, Children: ${parent.children?.length || 0}`);
        
        if (parent.children && parent.children.length > 0) {
            // Parent has children - process based on parent type
            
            if (parentType === 'CUSTOMER_PAYMENT' || parentType === 'MONEY_MOVEMENT_PAYMENT') {
                // This is a PAYMENT parent
                let paymentCharges = 0;
                let paymentCredits = 0;
                
                parent.children.forEach(child => {
                    const childAmount = Math.abs(amountInPence(child.trojan_amount || 0) / 100);
                    const childType = child.transaction_type;
                    
                    console.log(`  Child: ${childType}, Amount: £${childAmount}`);
                    
                    // Check if child is a charge
                    if (['ONE_OFF_CHARGE', 'METERED_CHARGE', 'FIXED_MONTHLY_CHARGE'].includes(childType)) {
                        paymentCharges += childAmount;
                        console.log(`    → Charge: £${childAmount}`);
                    } else {
                        // Everything else under a PAYMENT parent is a credit
                        paymentCredits += childAmount;
                        console.log(`    → Credit: £${childAmount}`);
                    }
                });
                
                if (isParentResolved) {
                    summary.resolved.charges += paymentCharges;
                    summary.resolved.credits += paymentCredits;
                    
                    // Total paid = charges - credits (but never negative)
                    const netPayment = Math.max(0, paymentCharges - paymentCredits);
                    summary.resolved.paid += netPayment;
                    
                    console.log(`  Resolved Payment: Charges £${paymentCharges}, Credits £${paymentCredits}, Net Paid £${netPayment}`);
                } else {
                    // Unresolved payment
                    const unresolvedCharges = paymentCharges;
                    const unresolvedCredits = paymentCredits;
                    
                    // Payments due = charges - credits
                    const netPaymentDue = unresolvedCharges - unresolvedCredits;
                    summary.unresolved.paymentsDue += netPaymentDue;
                    
                    console.log(`  Unresolved Payment: Charges £${unresolvedCharges}, Credits £${unresolvedCredits}, Net Due £${netPaymentDue}`);
                }
            } else if (parentType === 'CUSTOMER_REBATE') {
                // This is a REBATE parent
                let rebateAmount = 0;
                
                parent.children.forEach(child => {
                    const childAmount = Math.abs(amountInPence(child.trojan_amount || 0) / 100);
                    rebateAmount += childAmount;
                    console.log(`  Child (rebate): ${child.transaction_type}, Amount: £${childAmount}`);
                });
                
                if (isParentResolved) {
                    summary.resolved.reimbursed += rebateAmount;
                    console.log(`  Resolved Rebate: £${rebateAmount}`);
                } else {
                    summary.unresolved.reimbursementsDue += rebateAmount;
                    console.log(`  Unresolved Rebate Due: £${rebateAmount}`);
                }
            }
        } else {
            // Parent has no children - process as standalone transaction
            const parentAmount = Math.abs(amountInPence(parent.trojan_amount || 0) / 100);
            
            console.log(`  Standalone transaction, Amount: £${parentAmount}`);
            
            if (isParentResolved) {
                if (['METERED_CHARGE', 'ONE_OFF_CHARGE', 'FIXED_MONTHLY_CHARGE'].includes(parentType)) {
                    summary.resolved.charges += parentAmount;
                    summary.resolved.paid += parentAmount; // Standalone charge is also paid
                    console.log(`  → Resolved standalone charge (also counted as paid): £${parentAmount}`);
                } else if (parentType === 'CUSTOMER_REBATE') {
                    summary.resolved.reimbursed += parentAmount;
                    console.log(`  → Resolved standalone rebate: £${parentAmount}`);
                } else if (parentType === 'CUSTOMER_PAYMENT' || parentType === 'MONEY_MOVEMENT_PAYMENT') {
                    summary.resolved.paid += parentAmount;
                    console.log(`  → Resolved standalone payment: £${parentAmount}`);
                }
            } else {
                if (['METERED_CHARGE', 'ONE_OFF_CHARGE', 'FIXED_MONTHLY_CHARGE'].includes(parentType)) {
                    summary.unresolved.paymentsDue += parentAmount;
                    console.log(`  → Unresolved standalone charge: £${parentAmount}`);
                } else if (parentType === 'CUSTOMER_REBATE') {
                    summary.unresolved.reimbursementsDue += parentAmount;
                    console.log(`  → Unresolved standalone rebate: £${parentAmount}`);
                }
            }
        }
    });
    
    console.log('\n=== Final Summary ===');
    console.log('Resolved:');
    console.log(`  Charges: £${summary.resolved.charges.toFixed(2)}`);
    console.log(`  Credits: £${summary.resolved.credits.toFixed(2)}`);
    console.log(`  Paid: £${summary.resolved.paid.toFixed(2)}`);
    console.log(`  Reimbursed: £${summary.resolved.reimbursed.toFixed(2)}`);
    console.log('Unresolved:');
    console.log(`  Payments Due: £${summary.unresolved.paymentsDue.toFixed(2)}`);
    console.log(`  Reimbursements Due: £${summary.unresolved.reimbursementsDue.toFixed(2)}`);
    
    return summary;
};


export const AonStatement = () => {
    const { currentUser, currentUserIsCustomer, customerId } = useCurrentUser();
    const { customer } = useCustomer(customerId);
    const aonAccount = useAonAccountForCustomer(customerId);
    const statementWrapper = useAonStatementForAccount(aonAccount?.account_id);
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [expandedSections, setExpandedSections] = useState({});
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

    const toggleSection = (index) => {
        setExpandedSections(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    // PDF Generation Function
    const generatePDF = () => {
        setIsGeneratingPDF(true);
        
        try {
            const doc = new jsPDF('p', 'pt', 'a4'); // Portrait, points, A4
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 40;
            let yPos = margin;
            let currentPage = 1;

            // Color definitions (matching theme but print-friendly)
            const colors = {
                primary: [10, 37, 64],      // #0a2540
                accent: [0, 217, 192],       // #00d9c0
                blue: [30, 64, 175],         // #1e40af
                gray: [90, 108, 125],        // #5a6c7d
                lightGray: [232, 237, 242],  // #e8edf2
                black: [0, 0, 0],
                white: [255, 255, 255]
            };

            // Helper: Check if new content fits, add page if needed
            const checkPageBreak = (requiredSpace) => {
                if (yPos + requiredSpace > pageHeight - 60) {
                    doc.addPage();
                    currentPage++;
                    yPos = margin;
                    
                    // Add page number
                    doc.setFontSize(9);
                    doc.setTextColor(...colors.gray);
                    doc.text(`Page ${currentPage}`, pageWidth - margin, pageHeight - 30, { align: 'right' });
                    
                    return true;
                }
                return false;
            };

            // Helper: Format status text (no emojis)
            const getStatusText = (status) => {
                if (!status || status === null) return 'Scheduled';
                switch (status.toUpperCase()) {
                    case 'RESOLVED': return 'Resolved';
                    case 'PENDING': return 'Pending';
                    case 'FAILED': return 'Failed';
                    default: return 'Scheduled';
                }
            };

            // Helper: Clean text from emojis
            const cleanText = (text) => {
                if (!text) return '';
                return text
                    .replace(/📅|⚡|📍|🏠|🚗|⏱️|💳|💰|✓|⏳|✗|❕/g, '')
                    .replace(/\s+/g, ' ')
                    .trim();
            };

            // ===== HEADER SECTION (PLACEHOLDER) =====
            doc.setFillColor(...colors.primary);
            doc.rect(0, 0, pageWidth, 120, 'F');
            
            // Logo placeholder (left side)
            doc.setDrawColor(...colors.white);
            doc.setLineWidth(2);
            doc.rect(margin, 25, 60, 60, 'S'); // Square placeholder for logo
            doc.setFontSize(8);
            doc.setTextColor(...colors.white);
            doc.text('[LOGO]', margin + 30, 58, { align: 'center' });
            
            // Company info (left side, next to logo)
            doc.setTextColor(...colors.white);
            doc.setFontSize(24);
            doc.setFont('helvetica', 'bold');
            doc.text('TROJAN HOME', margin + 80, 45);
            
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text('[Company Address Line 1]', margin + 80, 65);
            doc.text('[Company Address Line 2]', margin + 80, 80);
            doc.text('[Contact Information]', margin + 80, 95);
            
            // Statement info (right aligned)
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text('STATEMENT', pageWidth - margin, 45, { align: 'right' });
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text(`Statement generated on ${new Date().toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            })}`, pageWidth - margin, 65, { align: 'right' });
            doc.text(`Customer: ${customer?.name || '[Customer Name]'}`, pageWidth - margin, 80, { align: 'right' });
            doc.text(`Account ID: ${aonAccount?.account_id || '[Account ID]'}`, pageWidth - margin, 95, { align: 'right' });
            
            yPos = 140;

            // ===== BALANCE SUMMARY =====
            checkPageBreak(100);
            
            doc.setFillColor(...colors.lightGray);
            doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 80, 8, 8, 'F');
            
            const balanceBoxWidth = (pageWidth - 2 * margin - 20) / 2;
            
            // You're Owed
            doc.setFontSize(10);
            doc.setTextColor(...colors.gray);
            doc.setFont('helvetica', 'normal');
            doc.text("You're Owed", margin + balanceBoxWidth / 2, yPos + 25, { align: 'center' });
            
            doc.setFontSize(20);
            doc.setTextColor(...colors.accent);
            doc.setFont('helvetica', 'bold');
            doc.text(
                formatCurrency('£', amountInPence(amount_owed_to_customer) / 100),
                margin + balanceBoxWidth / 2,
                yPos + 50,
                { align: 'center' }
            );
            
            // You Owe
            doc.setFontSize(10);
            doc.setTextColor(...colors.gray);
            doc.setFont('helvetica', 'normal');
            doc.text("You Owe", margin + balanceBoxWidth + 20 + balanceBoxWidth / 2, yPos + 25, { align: 'center' });
            
            doc.setFontSize(20);
            doc.setTextColor(...colors.blue);
            doc.setFont('helvetica', 'bold');
            doc.text(
                formatCurrency('£', amountInPence(amount_owed_to_trojan) / 100),
                margin + balanceBoxWidth + 20 + balanceBoxWidth / 2,
                yPos + 50,
                { align: 'center' }
            );
            
            yPos += 100;

            // ===== TRANSACTIONS SECTION =====
            checkPageBreak(60);
            
            doc.setFontSize(14);
            doc.setTextColor(...colors.primary);
            doc.setFont('helvetica', 'bold');
            doc.text('Transaction History', margin, yPos);
            yPos += 25;
            
            // Process each parent transaction
            statements.forEach((parent, pIdx) => {
                checkPageBreak(50);
                
                // Parent transaction row
                doc.setFillColor(...colors.lightGray);
                doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 40, 4, 4, 'F');
                
                // Date
                doc.setFontSize(9);
                doc.setTextColor(...colors.gray);
                doc.setFont('helvetica', 'normal');
                const dateText = parent.due_date && ['PENDING', 'SCHEDULED'].includes(parent.friendly_status?.toUpperCase() || 'SCHEDULED')
                    ? `Due: ${new Date(parent.due_date).toLocaleDateString('en-GB')}`
                    : `${parent.day_month || '-'}`;
                doc.text(dateText, margin + 10, yPos + 15);
                
                // Status
                const statusText = getStatusText(parent.friendly_status);
                doc.text(`(${statusText})`, margin + 10, yPos + 28);
                
                // Description
                doc.setFontSize(10);
                doc.setTextColor(...colors.primary);
                doc.setFont('helvetica', 'bold');
                let descText = parent.friendly_type;
                if (parent.children && parent.children.length > 0) {
                    descText += ` (${parent.child_count} items)`;
                }
                doc.text(descText, margin + 120, yPos + 15);
                
                // Transaction description
                if (parent.transaction_desc) {
                    doc.setFontSize(8);
                    doc.setTextColor(...colors.gray);
                    doc.setFont('helvetica', 'normal');
                    const cleanDesc = cleanText(parent.transaction_desc);
                    const descLines = doc.splitTextToSize(cleanDesc, 250);
                    doc.text(descLines[0], margin + 120, yPos + 28);
                }
                
                // Amount
                doc.setFontSize(12);
                doc.setTextColor(...colors.primary);
                doc.setFont('helvetica', 'bold');
                doc.text(
                    formatCurrency('£', amountInPence(parent.trojan_amount) / 100),
                    pageWidth - margin - 10,
                    yPos + 22,
                    { align: 'right' }
                );
                
                yPos += 50;
                
                // Child transactions
                if (parent.children && parent.children.length > 0) {
                    parent.children.forEach((child, cIdx) => {
                        checkPageBreak(45);
                        
                        // Child row (indented)
                        doc.setFillColor(248, 249, 251);
                        doc.rect(margin + 20, yPos, pageWidth - 2 * margin - 20, 38, 'F');
                        
                        // Left border accent
                        doc.setDrawColor(...colors.accent);
                        doc.setLineWidth(2);
                        doc.line(margin + 20, yPos, margin + 20, yPos + 38);
                        
                        // Date and status
                        doc.setFontSize(8);
                        doc.setTextColor(...colors.gray);
                        doc.setFont('helvetica', 'normal');
                        doc.text(`${child.day_month} ${child.time || ''}`, margin + 30, yPos + 12);
                        doc.text(`(${getStatusText(child.friendly_status)})`, margin + 30, yPos + 23);
                        
                        // Description
                        doc.setFontSize(9);
                        doc.setTextColor(...colors.primary);
                        doc.setFont('helvetica', 'bold');
                        doc.text(child.friendly_type, margin + 130, yPos + 12);
                        
                        // Details (consumption, location, etc.)
                        let detailsY = yPos + 23;
                        doc.setFontSize(7);
                        doc.setTextColor(...colors.gray);
                        doc.setFont('helvetica', 'normal');
                        
                        if (child.transaction_type === 'METERED_CHARGE' && child.consumption_kwh) {
                            const details = `Energy: ${child.consumption_kwh} kWh`;
                            doc.text(details, margin + 130, detailsY);
                            if (child.location) {
                                detailsY += 8;
                                const locationType = child.is_host_at_home ? '(Home)' : '(Away)';
                                doc.text(`Location: ${child.location} ${locationType}`, margin + 130, detailsY);
                            }
                        } else if (child.transaction_desc) {
                            const cleanDesc = cleanText(child.transaction_desc);
                            const descLines = doc.splitTextToSize(cleanDesc, 200);
                            doc.text(descLines[0], margin + 130, detailsY);
                        }
                        
                        // Amount
                        doc.setFontSize(11);
                        doc.setTextColor(...colors.primary);
                        doc.setFont('helvetica', 'bold');
                        doc.text(
                            formatCurrency('£', amountInPence(child.trojan_amount) / 100),
                            pageWidth - margin - 10,
                            yPos + 20,
                            { align: 'right' }
                        );
                        
                        yPos += 43;
                    });
                }
                
                yPos += 5; // Gap between transaction groups
            });

            // ===== NET SUMMARY SECTION =====
            checkPageBreak(200);
            
            yPos += 10;
            doc.setFontSize(14);
            doc.setTextColor(...colors.primary);
            doc.setFont('helvetica', 'bold');
            doc.text('Net Summary', margin, yPos);
            yPos += 25;
            
            // Resolved Section
            doc.setFontSize(11);
            doc.setTextColor(...colors.accent);
            doc.text('Resolved Transactions', margin, yPos);
            yPos += 20;
            
            const summaryData = [
                { label: 'Total Charges:', value: netSummary.resolved.charges },
                { label: 'Total Credits:', value: netSummary.resolved.credits },
                { label: 'Total Paid:', value: netSummary.resolved.paid },
                { label: 'Total Reimbursed:', value: netSummary.resolved.reimbursed }
            ];
            
            summaryData.forEach((item, idx) => {
                doc.setFillColor(idx % 2 === 0 ? 255 : 250, idx % 2 === 0 ? 255 : 252, idx % 2 === 0 ? 255 : 253);
                doc.rect(margin, yPos, pageWidth - 2 * margin, 25, 'F');
                
                doc.setFontSize(10);
                doc.setTextColor(...colors.gray);
                doc.setFont('helvetica', 'normal');
                doc.text(item.label, margin + 10, yPos + 16);
                
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(...colors.primary);
                doc.text(formatCurrency('£', item.value), pageWidth - margin - 10, yPos + 16, { align: 'right' });
                
                yPos += 25;
            });
            
            yPos += 15;
            checkPageBreak(100);
            
            // Unresolved Section
            doc.setFontSize(11);
            doc.setTextColor(...colors.blue);
            doc.setFont('helvetica', 'bold');
            doc.text('Unresolved Transactions', margin, yPos);
            yPos += 20;
            
            const unresolvedData = [
                { label: 'Total Payments Due:', value: netSummary.unresolved.paymentsDue },
                { label: 'Total Reimbursements Due:', value: netSummary.unresolved.reimbursementsDue }
            ];
            
            unresolvedData.forEach((item, idx) => {
                doc.setFillColor(idx % 2 === 0 ? 255 : 250, idx % 2 === 0 ? 255 : 252, idx % 2 === 0 ? 255 : 253);
                doc.rect(margin, yPos, pageWidth - 2 * margin, 25, 'F');
                
                doc.setFontSize(10);
                doc.setTextColor(...colors.gray);
                doc.setFont('helvetica', 'normal');
                doc.text(item.label, margin + 10, yPos + 16);
                
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(...colors.primary);
                doc.text(formatCurrency('£', item.value), pageWidth - margin - 10, yPos + 16, { align: 'right' });
                
                yPos += 25;
            });
            
            // Add page numbers to first page
            doc.setFontSize(9);
            doc.setTextColor(...colors.gray);
            if (currentPage === 1) {
                doc.text('Page 1', pageWidth - margin, pageHeight - 30, { align: 'right' });
            }
            
            // Save PDF
            const filename = `Trojan_Home_Statement_${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(filename);
            
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Failed to generate PDF. Please try again.');
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    // Redirect if not a customer
    if (currentUser && !currentUserIsCustomer) {
        history.push(`/404`);
        return null;
    }

    // Show loading while data is being fetched
    if (!statementWrapper) {
        return <div>Loading...</div>;
    }

    const { amount_owed_to_customer, amount_owed_to_trojan, statements = [] } = statementWrapper;

    // Calculate net summary from statements
    const netSummary = calculateNetSummary(statements);
    
    // Override unresolved amounts with API values
    netSummary.unresolved.paymentsDue = amountInPence(amount_owed_to_trojan) / 100;
    netSummary.unresolved.reimbursementsDue = amountInPence(amount_owed_to_customer) / 100;

    return (
        <div style={{ 
            minHeight: '100vh',
            background: '#00205b',
            paddingBottom: '2em'
        }}>
            <Container>
                <Grid centered padded>
                    <Grid.Column style={{ maxWidth: 500 }}>
                        {/* Header */}
                        <div style={{
                            textAlign: 'center',
                            padding: '2em 0 1em 0',
                            color: '#fff'
                        }}>
                            <Header as="h2" style={{ 
                                color: '#fff',
                                marginBottom: '0',
                                fontWeight: '700',
                                fontSize: '16px',
                                letterSpacing: '0'
                            }}>
                                Trojan Home - Statement
                            </Header>
                        </div>

                        {/* Balance Display - Two Cards Side by Side */}
                        <div style={{ 
                            display: 'flex',
                            gap: '20px',
                            marginBottom: '20px',
                            width: '100%'
                        }}>
                            <div style={{ 
                                flex: 1,
                                backgroundColor: '#00C5C2',
                                borderRadius: '6px',
                                padding: '28px 0',
                                textAlign: 'center',
                                minHeight: '120px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                            }}>
                                <div style={{ 
                                    fontSize: '16px', 
                                    color: '#00205b',
                                    marginBottom: '0.5em',
                                    fontWeight: '400'
                                }}>
                                    You're owed
                                </div>
                                <div style={{ 
                                    fontSize: '24px', 
                                    fontWeight: '700', 
                                    color: '#00205b'
                                }}>
                                    {formatCurrency('£', amountInPence(amount_owed_to_customer) / 100)}
                                </div>
                            </div>
                            <div style={{ 
                                flex: 1,
                                backgroundColor: '#1e40af',
                                borderRadius: '6px',
                                padding: '10px 0',
                                textAlign: 'center',
                                minHeight: '120px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                            }}>
                                <div style={{ 
                                    fontSize: '16px', 
                                    color: '#fff',
                                    marginBottom: '0.5em',
                                    fontWeight: '400'
                                }}>
                                    You owe
                                </div>
                                <div style={{ 
                                    fontSize: '24px', 
                                    fontWeight: '700', 
                                    color: '#fff'
                                }}>
                                    {formatCurrency('£', amountInPence(amount_owed_to_trojan) / 100)}
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div style={{ 
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            marginBottom: '20px',
                            width: '100%'
                        }}>
                            <div 
                                style={{ 
                                    backgroundColor: '#E9EBF1',
                                    borderRadius: '6px',
                                    padding: '20px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                                onClick={() => setShowSummaryModal(true)}
                            >
                                <span style={{ 
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    color: '#00205b'
                                }}>
                                    View Net Summary
                                </span>
                                <Icon name="chevron right" style={{ margin: 0, color: '#00205b' }} />
                            </div>
                            <div 
                                style={{ 
                                    backgroundColor: '#E9EBF1',
                                    borderRadius: '6px',
                                    padding: '20px',
                                    cursor: isGeneratingPDF ? 'not-allowed' : 'pointer',
                                    opacity: isGeneratingPDF ? 0.6 : 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                                onClick={isGeneratingPDF ? undefined : generatePDF}
                            >
                                <span style={{ 
                                    fontSize: '12px',
                                    fontWeight: '700',
                                    color: '#00205b'
                                }}>
                                    {isGeneratingPDF ? 'Generating PDF...' : 'Export to PDF'}
                                </span>
                                <Icon name="chevron right" style={{ margin: 0, color: '#00205b' }} />
                            </div>
                        </div>

                        {/* Transactions */}
                        <div>
                            {statements.map((parentTransaction, p_idx) => {
                                const isExpanded = expandedSections[p_idx] === true;
                                const hasChildren = parentTransaction.children && parentTransaction.children.length > 0;
                                
                                // Check if overdue
                                const dueDate = parentTransaction.due_date ? new Date(parentTransaction.due_date) : null;
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                if (dueDate) dueDate.setHours(0, 0, 0, 0);
                                const isOverdue = dueDate && dueDate < today && ['PENDING', 'SCHEDULED'].includes(parentTransaction.friendly_status?.toUpperCase() || 'SCHEDULED');

                                return (
                                    <div key={p_idx} style={{ marginBottom: '20px', width: '100%' }}>
                                        {/* Parent Transaction Card */}
                                        <div 
                                            style={{ 
                                                backgroundColor: '#E9EBF1',
                                                borderRadius: '6px',
                                                padding: '20px',
                                                marginBottom: hasChildren && isExpanded ? '2px' : '0',
                                                cursor: hasChildren ? 'pointer' : 'default'
                                            }}
                                            onClick={() => hasChildren && toggleSection(p_idx)}
                                        >
                                            {/* Top Row: Date/Type and Amount */}
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                marginBottom: '20px'
                                            }}>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ 
                                                        fontSize: '12px',
                                                        color: '#727272',
                                                        marginBottom: '4px'
                                                    }}>
                                                        {parentTransaction.friendly_type}
                                                    </div>
                                                    <div style={{ 
                                                        fontSize: '12px',
                                                        color: '#00205b',
                                                        fontWeight: '400'
                                                    }}>
                                                        {parentTransaction.due_date && ['PENDING', 'SCHEDULED'].includes(parentTransaction.friendly_status?.toUpperCase() || 'SCHEDULED')
                                                            ? `Due: ${new Date(parentTransaction.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
                                                            : `${parentTransaction.day_month || '-'}`
                                                        }
                                                    </div>
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    <div style={{ 
                                                        fontSize: '20px',
                                                        fontWeight: '700',
                                                        color: '#00205b',
                                                        lineHeight: '1'
                                                    }}>
                                                        {formatCurrency('£', amountInPence(parentTransaction.trojan_amount) / 100)}
                                                    </div>
                                                    {parentTransaction.total_consumption_kwh && (
                                                        <div style={{ 
                                                            fontSize: '12px',
                                                            color: '#00205b',
                                                            marginTop: '4px',
                                                            fontWeight: '400'
                                                        }}>
                                                            {parseFloat(parentTransaction.total_consumption_kwh).toFixed(2)} kWh
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Status Row */}
                                            <div style={{
                                                display: 'flex',
                                                gap: '10px',
                                                justifyContent: 'flex-end',
                                                marginBottom: '20px',
                                                minHeight: '20px'
                                            }}>
                                                {isOverdue && (
                                                    <div style={{
                                                        display: 'inline-block',
                                                        padding: '0 10px',
                                                        borderRadius: '6px',
                                                        backgroundColor: '#ff6b6b',
                                                        color: '#fff',
                                                        fontSize: '12px',
                                                        fontWeight: '700',
                                                        height: '20px',
                                                        lineHeight: '20px'
                                                    }}>
                                                        Overdue
                                                    </div>
                                                )}
                                                <StatusBadge status={parentTransaction.friendly_status} />
                                            </div>

                                            {/* Resolved/Description Message */}
                                            {parentTransaction.friendly_status?.toUpperCase() === 'RESOLVED' && parentTransaction.resolved_date ? (
                                                <div style={{ 
                                                    fontSize: '12px',
                                                    color: '#00205b',
                                                    padding: '10px',
                                                    backgroundColor: '#c5eaeb',
                                                    borderRadius: '6px',
                                                    marginBottom: hasChildren ? '20px' : '0'
                                                }}>
                                                    {parentTransaction.transaction_type === 'CUSTOMER_PAYMENT'
                                                        ? `Payment received ${new Date(parentTransaction.resolved_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
                                                        : parentTransaction.transaction_type === 'CUSTOMER_REBATE'
                                                        ? `Rebate issued ${new Date(parentTransaction.resolved_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
                                                        : parentTransaction.transaction_desc
                                                    }
                                                </div>
                                            ) : null}

                                            {/* Expandable Bottom Row (if has children) */}
                                            {hasChildren && (
                                                <div 
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <div style={{ 
                                                        fontSize: '12px',
                                                        fontWeight: '700',
                                                        color: '#00205b',
                                                        flex: 1,
                                                        textAlign: 'right'
                                                    }}>
                                                        {parentTransaction.friendly_type} <span style={{ fontWeight: '400' }}>({parentTransaction.child_count})</span>
                                                    </div>
                                                    <Icon 
                                                        name={isExpanded ? 'chevron down' : 'chevron right'}
                                                        style={{ margin: 0, marginLeft: '10px', color: '#00205b' }}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                {/* Child Transaction Rows */}
                                {isExpanded && hasChildren && parentTransaction.children.map((childTransaction, c_idx) => (
                                    <div 
                                        key={c_idx}
                                        style={{ 
                                            backgroundColor: 'rgba(255,255,255,0.75)',
                                            borderRadius: '6px',
                                            padding: '20px',
                                            marginBottom: '2px',
                                            boxShadow: 'inset 0px 4px 4px 0px rgba(0,0,0,0.25)',
                                            position: 'relative'
                                        }}
                                    >
                                        {/* Top Row: Date and Amount */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            marginBottom: '20px'
                                        }}>
                                            <div>
                                                <div style={{ 
                                                    fontSize: '12px',
                                                    color: '#00205b',
                                                    fontWeight: '400'
                                                }}>
                                                    {childTransaction.day_month}
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ 
                                                    fontSize: '20px',
                                                    fontWeight: '700',
                                                    color: '#00205b'
                                                }}>
                                                    {formatCurrency('£', amountInPence(childTransaction.trojan_amount) / 100)}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Transaction Details */}
                                        <TransactionDetails transaction={childTransaction} />

                                        {/* Bottom Row: Type and Status */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginTop: '20px'
                                        }}>
                                            <div style={{ 
                                                fontSize: '12px',
                                                fontWeight: '700',
                                                color: '#00205b',
                                                flex: 1
                                            }}>
                                                {childTransaction.friendly_type}
                                            </div>
                                            <StatusBadge status={childTransaction.friendly_status} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </Grid.Column>
        </Grid>
    </Container>
    
    {/* Account Summary Modal */}
    <Modal
        open={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        size="small"
    >
        <Modal.Header style={{ backgroundColor: '#0a2540', color: '#fff' }}>
            Net Summary
        </Modal.Header>
        <Modal.Content style={{ backgroundColor: '#f8f9fb' }}>
            <Modal.Description>
                {/* Resolved Section */}
                <div style={{ marginBottom: '2em' }}>
                    <Header as="h3" style={{ color: '#00C5C2', marginBottom: '1em', fontSize: '16px' }}>
                        Resolved
                    </Header>
                    <div style={{ 
                        backgroundColor: '#fff',
                        padding: '1.2em',
                        borderRadius: '12px',
                        fontSize: '1em',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}>
                        <div style={{ 
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '0.7em 0',
                            borderBottom: '1px solid #e8edf2'
                        }}>
                            <span style={{ color: '#5a6c7d', fontWeight: '500' }}>Total Charges:</span>
                            <span style={{ fontWeight: 'bold', color: '#0a2540' }}>
                                {formatCurrency('£', netSummary.resolved.charges)}
                            </span>
                        </div>
                        <div style={{ 
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '0.7em 0',
                            borderBottom: '1px solid #e8edf2'
                        }}>
                            <span style={{ color: '#5a6c7d', fontWeight: '500' }}>Total Credits:</span>
                            <span style={{ fontWeight: 'bold', color: '#00C5C2' }}>
                                {formatCurrency('£', netSummary.resolved.credits)}
                            </span>
                        </div>
                        <div style={{ 
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '0.7em 0',
                            borderBottom: '1px solid #e8edf2'
                        }}>
                            <span style={{ color: '#5a6c7d', fontWeight: '500' }}>Total Paid:</span>
                            <span style={{ fontWeight: 'bold', color: '#0a2540' }}>
                                {formatCurrency('£', netSummary.resolved.paid)}
                            </span>
                        </div>
                        <div style={{ 
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '0.7em 0'
                        }}>
                            <span style={{ color: '#5a6c7d', fontWeight: '500' }}>Total Reimbursed:</span>
                            <span style={{ fontWeight: 'bold', color: '#00C5C2' }}>
                                {formatCurrency('£', netSummary.resolved.reimbursed)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Unresolved Section */}
                <div>
                    <Header as="h3" style={{ color: '#1e40af', marginBottom: '1em', fontSize: '16px' }}>
                        Unresolved
                    </Header>
                    <div style={{ 
                        backgroundColor: '#fff',
                        padding: '1.2em',
                        borderRadius: '12px',
                        fontSize: '1em',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}>
                        <div style={{ 
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '0.7em 0',
                            borderBottom: '1px solid #e8edf2'
                        }}>
                            <span style={{ color: '#5a6c7d', fontWeight: '500' }}>Total Payments Due:</span>
                            <span style={{ fontWeight: 'bold', color: '#1e40af' }}>
                                {formatCurrency('£', netSummary.unresolved.paymentsDue)}
                            </span>
                        </div>
                        <div style={{ 
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '0.7em 0'
                        }}>
                            <span style={{ color: '#5a6c7d', fontWeight: '500' }}>Total Reimbursements Due:</span>
                            <span style={{ fontWeight: 'bold', color: '#00C5C2' }}>
                                {formatCurrency('£', netSummary.unresolved.reimbursementsDue)}
                            </span>
                        </div>
                    </div>
                </div>
            </Modal.Description>
        </Modal.Content>
        <Modal.Actions style={{ backgroundColor: '#f8f9fb' }}>
            <Button 
                onClick={() => setShowSummaryModal(false)}
                style={{
                    backgroundColor: '#00C5C2',
                    color: '#00205b',
                    borderRadius: '6px',
                    padding: '0.8em 2em',
                    fontWeight: '700',
                    border: 'none',
                    fontSize: '12px'
                }}
            >
                Close
            </Button>
        </Modal.Actions>
    </Modal>
</div>
    );
};
